import React from 'react';
import { Shield, Award, Clock, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

export const About = () => {
  const values = [
    {
      icon: Shield,
      title: 'Reliability',
      description: 'Punctual service you can count on, every single time.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'Premium vehicles and professional service standards.'
    },
    {
      icon: Users,
      title: 'Customer Focus',
      description: 'Your comfort and satisfaction are our top priorities.'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl sm:text-6xl font-bold mb-6">About LiveryRUs</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Your trusted partner for luxury airport transportation since 2004.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Over 20 Years of Excellence
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed text-lg">
                <p>
                  Since 2004, LiveryRUs has proudly provided luxury airport transportation throughout Chicago and the entire Chicagoland area. Our commitment to excellence has made us the preferred choice for discerning travelers who demand reliability, comfort, and professionalism.
                </p>
                <p>
                  Specializing in O'Hare International Airport transfers, our professional chauffeurs, clean vehicles, and punctual service make every ride comfortable and stress-free. Whether you're traveling for business or pleasure from Chicago's northern suburbs, we ensure your journey begins and ends on the right note.
                </p>
                <p>
                  We understand that your time is valuable. That's why we offer flat-rate pricing with no hidden fees, flexible scheduling, and a fleet of premium vehicles to meet your needs. From executive sedans to spacious SUVs, we have the right vehicle for every Chicago area transportation need.
                </p>
              </div>
            </div>

            {/* Image */}
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_86bfc4f8-b687-473a-9179-c22fa1fae018/artifacts/qmg6bn5d_image.png"
                alt="LiveryRUs professional Chicago limo service vehicle"
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#00D4FF] text-black p-8 rounded-lg shadow-xl">
                <div className="text-5xl font-bold mb-2">20+</div>
                <div className="text-sm font-semibold uppercase tracking-wide">Years of Service</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <div className="w-24 h-1 bg-[#00D4FF] mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card
                  key={index}
                  className="group hover:shadow-2xl transition-all duration-300 border-0 shadow-lg hover:-translate-y-2"
                >
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#00D4FF] transition-colors duration-300">
                      <Icon className="w-8 h-8 text-[#00D4FF] group-hover:text-black transition-colors duration-300" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {value.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Why Travelers Choose LiveryRUs
          </h2>
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-[#00D4FF] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-black font-bold">1</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Professional Chauffeurs</h3>
                <p className="text-gray-700">All our drivers are licensed, insured, and background-checked. They undergo regular training to ensure the highest standards of service and safety.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-[#00D4FF] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-black font-bold">2</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Transparent Pricing</h3>
                <p className="text-gray-700">No surge pricing, no hidden fees. Our flat-rate pricing means you know exactly what you'll pay before you book.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-[#00D4FF] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-black font-bold">3</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Premium Fleet</h3>
                <p className="text-gray-700">Our vehicles are meticulously maintained and regularly updated to ensure your comfort and safety throughout your journey.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-[#00D4FF] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-black font-bold">4</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Flexible Scheduling</h3>
                <p className="text-gray-700">Early morning flight or late-night arrival? We're here whenever you need us, 365 days a year.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};